/*
 * Карточка «Алерты» вкладки метрик: активные алерты Prometheus кластера, относящиеся к
 * сущности (lib/alerts), по одной строке на алерт — состояние, имя, важность, суть,
 * с какого времени. Клик по строке раскрывает description целиком, метки и значение.
 */
import { Fragment, KeyboardEvent, useState } from 'react';
import {
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  StatusError,
  StatusPending,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import {
  AlertMatcher,
  PromAlert,
  alertDescription,
  alertSummary,
  countAlerts,
  fetchAlerts,
} from '../../lib/alerts';

const useStyles = makeStyles(theme => ({
  row: { cursor: 'pointer', '& > td': { borderBottom: 'none' } },
  name: { fontWeight: 700, whiteSpace: 'nowrap' },
  summary: {
    maxWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  nowrap: { whiteSpace: 'nowrap' },
  chip: { height: 22, fontSize: 11.5, fontWeight: 700 },
  critical: { background: theme.palette.status.error, color: '#fff' },
  warning: { background: theme.palette.status.warning, color: '#fff' },
  info: { background: theme.palette.action.selected },
  expand: {
    transition: 'transform .15s',
    color: theme.palette.text.secondary,
  },
  expanded: { transform: 'rotate(180deg)' },
  detailsCell: { paddingTop: 0, paddingBottom: theme.spacing(1.5) },
  // свёрнутая строка подробностей — только разделитель, без высоты
  collapsedCell: { padding: 0 },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(4.5),
  },
  description: { whiteSpace: 'pre-wrap' },
  labels: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  label: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5 },
  muted: { color: theme.palette.text.secondary, fontSize: 13 },
}));

const since = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Severity = ({ severity }: { severity?: string }) => {
  const classes = useStyles();
  if (!severity) return null;
  const tones: Record<string, string> = {
    critical: classes.critical,
    error: classes.critical,
    warning: classes.warning,
  };
  const tone = tones[severity] ?? classes.info;
  return (
    <Chip size="small" label={severity} className={`${classes.chip} ${tone}`} />
  );
};

const State = ({ state }: { state: string }) =>
  state === 'firing' ? (
    <StatusError>firing</StatusError>
  ) : (
    <StatusPending>{state}</StatusPending>
  );

const AlertRow = ({ alert }: { alert: PromAlert }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(o => !o);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };
  const description = alertDescription(alert);
  const runbook = alert.annotations.runbook_url;
  const labels = Object.entries(alert.labels).filter(
    ([k]) => k !== 'alertname',
  );
  return (
    <Fragment>
      <TableRow
        hover
        className={classes.row}
        onClick={toggle}
        onKeyDown={onKey}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        <TableCell padding="checkbox">
          <ExpandMoreIcon
            fontSize="small"
            className={`${classes.expand} ${open ? classes.expanded : ''}`}
          />
        </TableCell>
        <TableCell className={classes.nowrap}>
          <State state={alert.state} />
        </TableCell>
        <TableCell className={classes.name}>{alert.name}</TableCell>
        <TableCell className={classes.nowrap}>
          <Severity severity={alert.severity} />
        </TableCell>
        <TableCell className={classes.summary} title={alertSummary(alert)}>
          {alertSummary(alert)}
        </TableCell>
        <TableCell className={`${classes.nowrap} ${classes.muted}`}>
          {since(alert.activeAt)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={6}
          className={open ? classes.detailsCell : classes.collapsedCell}
        >
          <Collapse in={open} unmountOnExit>
            <div className={classes.details}>
              <Typography variant="body2" className={classes.description}>
                {description ?? alertSummary(alert)}
              </Typography>
              {runbook && (
                <Typography variant="body2">
                  <Link to={runbook}>Runbook</Link>
                </Typography>
              )}
              {alert.value && (
                <Typography variant="body2" className={classes.muted}>
                  Значение: {alert.value}
                </Typography>
              )}
              <div className={classes.labels}>
                {labels.map(([k, v]) => (
                  <Chip
                    key={k}
                    size="small"
                    variant="outlined"
                    label={`${k}=${v}`}
                    className={classes.label}
                  />
                ))}
              </div>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export const AlertsCard = ({
  cluster,
  match,
}: {
  /** Кластер платформы, чей Prometheus спрашиваем. */
  cluster: string;
  /** Какие алерты относятся к сущности; без фильтра — все алерты кластера. */
  match?: AlertMatcher;
}) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { value, loading, error } = useAsync(
    () => fetchAlerts({ discoveryApi, fetchApi }, cluster),
    [cluster],
  );
  if (loading) {
    return (
      <InfoCard title="Алерты">
        <Progress />
      </InfoCard>
    );
  }
  if (error) {
    return (
      <ResponseErrorPanel
        title="Не удалось загрузить алерты Prometheus"
        error={error}
      />
    );
  }
  const alerts = (value ?? []).filter(match ?? (() => true));
  const { firing, pending } = countAlerts(alerts);
  const subheader =
    alerts.length === 0
      ? 'активных алертов нет'
      : [firing && `firing ${firing}`, pending && `pending ${pending}`]
          .filter(Boolean)
          .join(' · ');
  return (
    <InfoCard title="Алерты" subheader={subheader}>
      {alerts.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Состояние</TableCell>
              <TableCell>Алерт</TableCell>
              <TableCell>Важность</TableCell>
              <TableCell>Суть</TableCell>
              <TableCell>С</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.map((a, i) => (
              <AlertRow key={`${a.name}-${i}`} alert={a} />
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="body2" className={classes.muted}>
        {alerts.length === 0
          ? 'Prometheus кластера не сообщает об активных алертах по этой сущности.'
          : 'Клик по строке — полное описание, метки и значение.'}
      </Typography>
    </InfoCard>
  );
};
